import React, { useMemo, useCallback } from "react";
import "./ScheduleGrid.css";
import { getCategoryColors } from "./categoryColorHelper";

// Utility: parse date string or Date -> Date
function asDate(v) {
  if (v instanceof Date) return v;
  return new Date(v);
}

// Determine overlapping lanes similar to the Blazor implementation
function buildLanes(events) {
  const lanes = []; // array of arrays
  const laneIndexById = new Map();
  const overlaps = (a, b) => a.startTime < b.endTime && b.startTime < a.endTime;

  events.forEach((ev) => {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      const last = lanes[i][lanes[i].length - 1];
      if (!overlaps(last, ev)) {
        lanes[i].push(ev);
        laneIndexById.set(ev.id, i);
        placed = true;
        break;
      }
    }
    if (!placed) {
      lanes.push([ev]);
      laneIndexById.set(ev.id, lanes.length - 1);
    }
  });
  return { lanes, laneIndexById };
}

export function ScheduleGrid({
  dayStart,
  dayEnd,
  sections = [],
  events = [],
  slotMinutes = 30,
  slotHeightPx = 40,
  timeFormat = "HH:mm",
  defaultEventColor = "#d9e1f2",
  timeLabelStepMinutes = 30,
  timeColumnWidthPx = 70,
  onEventSelect,
}) {
  const dStart = asDate(dayStart);
  const dEnd = asDate(dayEnd);
  const totalMinutes = Math.ceil((dEnd - dStart) / 60000);
  const pxPerMinute = slotHeightPx / slotMinutes;

  const timeSlots = useMemo(() => {
    const arr = [];
    let t = dStart.getTime();
    const end = dEnd.getTime();
    const stepMs = slotMinutes * 60000;
    while (t < end) {
      arr.push(new Date(t));
      t += stepMs;
    }
    return arr;
  }, [dStart, dEnd, slotMinutes]);

  // Normalize events (ensure Date objects + sorted per section)
  const eventsBySection = useMemo(() => {
    const map = new Map();
    sections.forEach((s) => map.set(s.id, []));
    events.forEach((e) => {
      const item = {
        id: e.id || e.Id || crypto.randomUUID(),
        title: e.title || e.Title || "",
        startTime: asDate(e.startTime || e.StartTime),
        endTime: asDate(e.endTime || e.EndTime),
        sectionId: e.sectionId || e.SectionId,
        category: e.category || e.Category,
        eventTypeLabel: e.eventTypeLabel || e.EventTypeLabel,
        categoryLabel: e.categoryLabel || e.CategoryLabel,
        speaker: e.speaker || e.Speaker,
        color: e.color || e.Color,
        foregroundColor: e.foregroundColor || e.ForegroundColor,
        borderColor: e.borderColor || e.BorderColor,
        headerColor: e.headerColor || e.HeaderColor,
        headerTextColor: e.headerTextColor || e.HeaderTextColor,
        hideTitle: e.hideTitle || e.HideTitle,
        annotation: e.annotation || e.Annotation || "",
      };
      if (!map.has(item.sectionId)) map.set(item.sectionId, []);
      map.get(item.sectionId).push(item);
    });
    for (const arr of map.values()) {
      arr.sort((a, b) => a.startTime - b.startTime);
    }
    return map;
  }, [sections, events]);

  // Compute min width per section based on lane counts
  const sectionWidths = useMemo(() => {
    return sections.map((s) => {
      const evts = eventsBySection.get(s.id) || [];
      if (!evts.length) return 160; // base width
      const { lanes } = buildLanes(evts);
      return Math.max(1, lanes.length) * 160;
    });
  }, [sections, eventsBySection]);

  const gridTemplateColumns = useMemo(() => {
    return [
      `${timeColumnWidthPx}px`,
      ...sectionWidths.map((w) => w + "px"),
    ].join(" ");
  }, [sectionWidths, timeColumnWidthPx]);

  const formatTime = useCallback(
    (dt) => {
      // Minimal HH:mm formatter without external libs
      const h = dt.getHours().toString().padStart(2, "0");
      const m = dt.getMinutes().toString().padStart(2, "0");
      return timeFormat === "HH:mm" ? `${h}:${m}` : dt.toLocaleTimeString();
    },
    [timeFormat]
  );

  return (
    <div className="schedule-grid">
      <div className="sg-header" style={{ gridTemplateColumns }}>
        <div className="sg-time-header" />
        {sections.map((s) => (
          <div key={s.id} className="sg-section-header">
            {s.name || s.Name}
          </div>
        ))}
      </div>
      <div className="sg-body" style={{ gridTemplateColumns }}>
        {/* Time column */}
        <div className="sg-time-col">
          {timeSlots.map((ts) => {
            const show = ts.getMinutes() % timeLabelStepMinutes === 0;
            return (
              <div
                key={ts.toISOString()}
                className="sg-time-slot"
                style={{ height: slotHeightPx }}
              >
                {show ? formatTime(ts) : ""}
              </div>
            );
          })}
        </div>
        {sections.map((section, idx) => {
          const evts = eventsBySection.get(section.id) || [];
          const { laneIndexById } = buildLanes(evts); // Only need lane index mapping here; width precomputed
          const colHeightPx = totalMinutes * pxPerMinute;
          return (
            <div
              key={section.id}
              className="sg-section-col"
              style={{ height: colHeightPx }}
            >
              {timeSlots.map((_, i) => (
                <div
                  key={i}
                  className="sg-slot-bg"
                  style={{ height: slotHeightPx }}
                />
              ))}
              {evts.map((ev) => {
                const startMinutes = (ev.startTime - dStart) / 60000;
                const endMinutes = (ev.endTime - dStart) / 60000;
                if (endMinutes <= 0 || startMinutes >= totalMinutes)
                  return null;
                const top = Math.max(0, startMinutes) * pxPerMinute;
                const height = Math.max(
                  2,
                  (Math.min(endMinutes, totalMinutes) -
                    Math.max(0, startMinutes)) *
                    pxPerMinute
                );
                const laneIndex = laneIndexById.get(ev.id) || 0;
                const laneWidth = 160; // includes margin; lane count determines section min width earlier
                const eventWidth = 156; // visual width
                const left = laneWidth * laneIndex;
                // Prefer explicit event color, else category color, lastly fallback default
                const catColors = getCategoryColors(ev.category);
                const colorBase = ev.color || catColors.bg || defaultEventColor;
                const fg = ev.foregroundColor || catColors.fg;
                const border = ev.borderColor || catColors.border;
                const headerBg = ev.headerColor || border;
                const headerFg = ev.headerTextColor || fg;
                return (
                  <div
                    key={ev.id}
                    className="sg-event"
                    title={ev.title}
                    style={{
                      top,
                      height,
                      left,
                      width: eventWidth,
                      backgroundColor: colorBase,
                      color: fg,
                      border: `1px solid ${border}`,
                      marginLeft: 2,
                      marginRight: 2,
                      cursor: "pointer",
                    }}
                    onClick={() => onEventSelect && onEventSelect(ev)}
                  >
                    {(ev.categoryLabel || ev.eventTypeLabel) && (
                      <div
                        className="sg-event-header"
                        style={{
                          height: slotHeightPx,
                          backgroundColor: headerBg,
                          color: headerFg,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                        }}
                      >
                        {ev.categoryLabel && <span>{ev.categoryLabel}</span>}
                        {ev.categoryLabel && ev.eventTypeLabel && (
                          <span style={{ margin: "0 4px" }}>-</span>
                        )}
                        {ev.eventTypeLabel && <span>{ev.eventTypeLabel}</span>}
                      </div>
                    )}
                    {height !== slotHeightPx && !ev.hideTitle && (
                      <div className="sg-event-body">
                        <div className="sg-event-title">
                          {ev.title}
                          {ev.speaker && <span> - {ev.speaker}</span>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ScheduleGrid;
